import type { ComponentType } from "react";
import { cmtsRouteManifest } from "../core/routes/cmtsRouteManifest.js";
import type { CmtsRouteDefinition as CmtsManifestRouteDefinition } from "../core/routes/cmtsRouteTypes.js";
import { isPublicCmtsPermission } from "./permission.config.js";

export type CmtsRouteDomain =
  | "auth"
  | "workspace"
  | "administration"
  | "master-data"
  | "complaints"
  | "rca-capa"
  | "dashboards"
  | "reports"
  | "audit-security"
  | "governance"
  | "system";

export type CmtsRouteAccess = {
  readonly public?: boolean | undefined;
  readonly requiresAuth?: boolean | undefined;
  readonly requiredPermission?: string | undefined;
  readonly requiredActionCode?: string | undefined;
  readonly requiredAnyPermission?: readonly string[] | undefined;
  readonly requiredAllPermissions?: readonly string[] | undefined;
};

export type CmtsLazyRouteModule = {
  readonly Component: ComponentType<{ runtime: import("../../app/appRuntime.js").CmtsHostRuntime }>;
};

export type CmtsDataRouteDefinition = {
  readonly id: string;
  readonly path: string;
  readonly title: string;
  readonly domain: CmtsRouteDomain;
  readonly access: CmtsRouteAccess;
  readonly lazy: () => Promise<CmtsLazyRouteModule>;
  readonly fallbackKey?: string | undefined;
  readonly menuKey?: string | undefined;
  readonly parentMenuKey?: string | undefined;
  readonly pageConfigKey?: string | undefined;
  readonly chunkGroup?: string | undefined;
  readonly manifest: CmtsManifestRouteDefinition;
};


function domainFor(route: CmtsManifestRouteDefinition): CmtsRouteDomain {
  const text = `${route.module} ${route.feature} ${route.path}`.toLowerCase();
  if (route.permission === "PUBLIC" || text.includes("login") || text.includes("auth")) return "auth";
  if (text.includes("master-data") || text.includes("reference")) return "master-data";
  if (text.includes("administration") || text.includes("admin") || text.includes("organization") || text.includes("routing") || text.includes("configuration") || text.includes("scheduled")) return "administration";
  if (text.includes("complaint")) return "complaints";
  if (text.includes("rca") || text.includes("capa") || text.includes("verification")) return "rca-capa";
  if (text.includes("dashboard")) return "dashboards";
  if (text.includes("report")) return "reports";
  if (text.includes("audit") || text.includes("security") || text.includes("access")) return "audit-security";
  if (text.includes("governance")) return "governance";
  if (text.includes("workspace") || text.includes("task") || text.includes("message") || text.includes("notification")) return "workspace";
  return "system";
}

function chunkGroupFor(domain: CmtsRouteDomain): string {
  switch (domain) {
    case "administration":
    case "master-data":
    case "complaints":
    case "rca-capa":
    case "dashboards":
    case "reports":
    case "audit-security":
      return domain;
    case "auth":
      return "auth";
    default:
      return "workspace-system";
  }
}

const cmtsRouteComponentLoaders: Record<string, () => Promise<CmtsLazyRouteModule>> = {
  CmtsAccessGovernanceContainer: () => import("../route-modules/administration/CmtsAccessGovernanceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsTenantContainer: () => import("../route-modules/administration/CmtsTenantContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsOrganizationUnitContainer: () => import("../route-modules/administration/CmtsOrganizationUnitContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsUsersContainer: () => import("../route-modules/administration/CmtsUsersContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsRolesContainer: () => import("../route-modules/administration/CmtsRolesContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsPermissionsContainer: () => import("../route-modules/administration/CmtsPermissionsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsAdminCalendarSchedulerContainer: () => import("../route-modules/governance/CmtsAdminCalendarSchedulerContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsAdminEvidenceContainer: () => import("../route-modules/administration/CmtsAdminEvidenceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsAdminNotificationsContainer: () => import("../route-modules/administration/CmtsAdminNotificationsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsAlertsContainer: () => import("../route-modules/system/CmtsAlertsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsAuditLogsContainer: () => import("../route-modules/complaints/CmtsAuditLogsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsBackendEndpointCoverageContainer: () => import("../route-modules/audit-security/CmtsBackendEndpointCoverageContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsCapaContainer: () => import("../route-modules/rca-capa/CmtsCapaContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintCreateContainer: () => import("../route-modules/complaints/CmtsComplaintCreateContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintDetailContainer: () => import("../route-modules/complaints/CmtsComplaintDetailContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintRegisterContainer: () => import("../route-modules/complaints/CmtsComplaintRegisterContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsConfigurationGovernanceContainer: () => import("../route-modules/administration/CmtsConfigurationGovernanceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsDashboardsContainer: () => import("../route-modules/dashboards/CmtsDashboardsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsEscalationContainer: () => import("../route-modules/complaints/CmtsEscalationContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsEvidenceContainer: () => import("../route-modules/complaints/CmtsEvidenceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsFieldSchemaAdminContainer: () => import("../route-modules/administration/CmtsFieldSchemaAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsLoginContainer: () => import("../route-modules/auth/CmtsLoginContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsMasterDataAdminContainer: () => import("../route-modules/master-data/CmtsMasterDataAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsSeverityContainer: () => import("../route-modules/master-data/CmtsSeverityContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsPriorityContainer: () => import("../route-modules/master-data/CmtsPriorityContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintCategoryContainer: () => import("../route-modules/master-data/CmtsComplaintCategoryContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintSubcategoryContainer: () => import("../route-modules/master-data/CmtsComplaintSubcategoryContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsComplaintSourceContainer: () => import("../route-modules/master-data/CmtsComplaintSourceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsReferenceDataContainer: () => import("../route-modules/master-data/CmtsReferenceDataContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  ReferenceTypeContainer: () => import("../route-modules/master-data/ReferenceTypeContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsMasterDependencyContainer: () => import("../route-modules/governance/CmtsMasterDependencyContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsMessagesContainer: () => import("../route-modules/workspace/CmtsMessagesContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsMyWorkspaceContainer: () => import("../route-modules/workspace/CmtsMyWorkspaceContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsNotificationsContainer: () => import("../route-modules/workspace/CmtsNotificationsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsOperationalReadinessContainer: () => import("../route-modules/audit-security/CmtsOperationalReadinessContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsOrganizationAdminContainer: () => import("../route-modules/administration/CmtsOrganizationAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsRcaContainer: () => import("../route-modules/rca-capa/CmtsRcaContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsReferenceAdminContainer: () => import("../route-modules/master-data/CmtsReferenceAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsReportExportsContainer: () => import("../route-modules/reports/CmtsReportExportsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsReportsContainer: () => import("../route-modules/reports/CmtsReportsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsRoutingAdminContainer: () => import("../route-modules/administration/CmtsRoutingAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsScheduledJobsContainer: () => import("../route-modules/governance/CmtsScheduledJobsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsSecurityLogsContainer: () => import("../route-modules/audit-security/CmtsSecurityLogsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsSlaContainer: () => import("../route-modules/complaints/CmtsSlaContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsSodAdminContainer: () => import("../route-modules/governance/CmtsSodAdminContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsSystemOperationsContainer: () => import("../route-modules/administration/CmtsSystemOperationsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsTasksContainer: () => import("../route-modules/workspace/CmtsTasksContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsTypedReportsContainer: () => import("../route-modules/reports/CmtsTypedReportsContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsVerificationContainer: () => import("../route-modules/rca-capa/CmtsVerificationContainerRoute.js") as Promise<CmtsLazyRouteModule>,
  CmtsWorkflowContainer: () => import("../route-modules/complaints/CmtsWorkflowContainerRoute.js") as Promise<CmtsLazyRouteModule>,
};

function lazyContainer(exportName: string | undefined, routeId: string): () => Promise<CmtsLazyRouteModule> {
  return async () => {
    if (!exportName) {
      throw new Error(`CMTS_ROUTE_COMPONENT_EXPORT_MISSING:${routeId}`);
    }
    const loader = cmtsRouteComponentLoaders[exportName];
    if (!loader) {
      throw new Error(`CMTS_ROUTE_COMPONENT_EXPORT_NOT_REGISTERED:${routeId}:${exportName}`);
    }
    return loader();
  };
}


export const cmtsRouteDefinitions: readonly CmtsDataRouteDefinition[] = cmtsRouteManifest
  .filter((route) => Boolean(route.componentExport))
  .map((route) => {
    const publicRoute = isPublicCmtsPermission(route.permission);
    const domain = domainFor(route);
    return {
      id: route.id,
      path: route.path,
      title: route.title,
      domain,
      access: {
        public: publicRoute,
        requiresAuth: !publicRoute,
        requiredPermission: publicRoute ? undefined : route.permission,
      },
      lazy: lazyContainer(route.componentExport, route.id),
      fallbackKey: `${domain}.fallback`,
      menuKey: route.showInMenu ? route.id : undefined,
      parentMenuKey: route.menuGroupId,
      pageConfigKey: route.feature,
      chunkGroup: chunkGroupFor(domain),
      manifest: route,
    } satisfies CmtsDataRouteDefinition;
  });

export function getCmtsRegisteredPaths(): readonly string[] {
  return cmtsRouteDefinitions.map((route) => route.path);
}

export function findCmtsRouteDefinition(path: string): CmtsDataRouteDefinition | undefined {
  return cmtsRouteDefinitions.find((route) => route.path === path);
}

export function isRegisteredCmtsRoutePath(path: string): boolean {
  return getCmtsRegisteredPaths().includes(path);
}

export { cmtsRouteManifest };
